import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users2, Pencil, Trash2, Search, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import ImportDialog from "@/components/ImportDialog";
import CategoryFilter from "@/components/CategoryFilter";
import { useSeason } from "@/context/SeasonContext";
import { useSeasonCategories } from "@/hooks/useSeasonCategories";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const WEAPON_CODES = ["fioretto", "spada", "sciabola"];
const cap = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
const empty = { name: "", weapon: null, weapons: [], level: "principianti", schedule: "", coach_id: null };

function GroupWeaponSummary({ weapons = [], primary = null }) {
  const ws = (weapons || []).filter(Boolean);
  if (ws.length === 0) {
    return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-zinc-100 text-zinc-600 rounded" data-testid="group-weapons-general">Attività generale</span>;
  }
  if (ws.length === 1) return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-black text-white rounded" data-testid={`group-w-${ws[0]}`}>{cap(ws[0])}</span>;
  if (ws.length === 2) return (
    <div className="flex gap-1" data-testid="group-weapons-two">
      {ws.map((w) => (
        <span key={w} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${w === primary ? "bg-black text-white" : "bg-zinc-100 text-zinc-700 border border-zinc-300"}`}>{cap(w)}</span>
      ))}
    </div>
  );
  return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#0052FF] text-white rounded" data-testid="group-weapons-three">3 armi</span>;
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [memberSel, setMemberSel] = useState(new Set());
  const [initialMembers, setInitialMembers] = useState(new Set());
  const [memberSearch, setMemberSearch] = useState("");
  const [memberFilter, setMemberFilter] = useState("all");
  const [memberWeapon, setMemberWeapon] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [weaponFilter, setWeaponFilter] = useState("all");
  const { selectedSeasonId } = useSeason();
  const { categories, filterByCategory } = useSeasonCategories(selectedSeasonId);

  const load = async () => {
    const [g, a] = await Promise.all([api.get("/groups"), api.get("/athletes")]);
    setGroups(g.data); setAthletes(a.data);
    try {
      const u = await api.get("/users");
      setCoaches(u.data.filter((x) => x.role === "coach" || x.role === "admin"));
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  // FASE A · fonte unica "attivi" = archived !== true (stessa regola pagina Atleti)
  const activeAthletes = athletes.filter((a) => !a.archived);

  const openNew = () => { setForm({ ...empty, weapons: [] }); setEditingId(null); setOpen(true); };
  const effGroups = (a) => (a.group_ids?.length ? a.group_ids : (a.group_id ? [a.group_id] : []));

  const openEdit = (g) => {
    // Backfill weapons[] from legacy weapon if empty
    const weapons = (g.weapons && g.weapons.length) ? g.weapons : (g.weapon ? [g.weapon] : []);
    setForm({ ...empty, ...g, weapons, weapon: g.weapon || (weapons[0] || null) });
    setEditingId(g.id);
    const members = new Set(activeAthletes.filter((a) => effGroups(a).includes(g.id)).map((a) => a.id));
    setMemberSel(new Set(members));
    setInitialMembers(new Set(members));
    setMemberSearch(""); setMemberFilter("all"); setMemberWeapon("all");
    setOpen(true);
  };

  const toggleWeapon = (code) => {
    setForm((f) => {
      const has = f.weapons.includes(code);
      const next = has ? f.weapons.filter((w) => w !== code) : [...f.weapons, code];
      // Auto sync primary
      let primary = f.weapon;
      if (has && primary === code) primary = next[0] || null;
      if (!has && !primary) primary = code;
      if (next.length === 0) primary = null;
      return { ...f, weapons: next, weapon: primary };
    });
  };

  const setPrimary = (code) => {
    setForm((f) => ({ ...f, weapon: code, weapons: f.weapons.includes(code) ? f.weapons : [...f.weapons, code] }));
  };

  const save = async () => {
    try {
      const body = { ...form, weapons: form.weapons, weapon: form.weapon || null };
      let gid = editingId;
      if (editingId) {
        await api.put(`/groups/${editingId}`, { ...body, id: editingId });
      } else {
        const res = await api.post("/groups", body);
        gid = res.data?.id;
      }
      // Bulk membership diff (solo in modifica, su group_ids[] — mai overwrite altre membership)
      if (editingId && gid) {
        const add_ids = [...memberSel].filter((id) => !initialMembers.has(id));
        const remove_ids = [...initialMembers].filter((id) => !memberSel.has(id));
        if (add_ids.length || remove_ids.length) {
          await api.post(`/groups/${gid}/membership`, { add_ids, remove_ids });
        }
      }
      toast.success(editingId ? "Gruppo aggiornato" : "Gruppo creato");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch { toast.error("Errore"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/groups/${confirmDelete.id}`);
      toast.success("Gruppo eliminato");
      setConfirmDelete(null); load();
    } catch { toast.error("Errore"); }
  };

  const groupWeapons = (g) => (g.weapons && g.weapons.length) ? g.weapons : (g.weapon ? [g.weapon] : []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Gruppi</h1>
          <p className="text-zinc-500 mt-1">Squadre e categorie del club.</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportDialog entity="groups" onDone={load} />
          <Button data-testid="new-group-btn" onClick={openNew} className="h-12 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuovo gruppo</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <CategoryFilter categories={categories} value={categoryFilter} onChange={setCategoryFilter} testId="groups-category-filter" />
        <Select value={weaponFilter} onValueChange={setWeaponFilter}>
          <SelectTrigger data-testid="groups-weapon-filter" className="w-40 h-11"><SelectValue placeholder="Arma" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le armi</SelectItem>
            <SelectItem value="fioretto">Fioretto</SelectItem>
            <SelectItem value="spada">Spada</SelectItem>
            <SelectItem value="sciabola">Sciabola</SelectItem>
            <SelectItem value="general">Attività generale</SelectItem>
          </SelectContent>
        </Select>
        {(categoryFilter !== "all" || weaponFilter !== "all") && (
          <span className="text-xs text-zinc-500">Filtri attivi</span>
        )}
      </div>

      {(() => {
        const total = activeAthletes.length;
        const assigned = activeAthletes.filter((a) => effGroups(a).length > 0).length;
        return (
          <div className="flex items-center gap-4 text-sm bg-white border border-zinc-200 rounded-md px-4 py-3" data-testid="groups-roster-summary">
            <span><span className="font-mono text-lg font-bold">{total}</span> atleti</span>
            <span className="text-zinc-300">·</span>
            <span className="text-emerald-700"><span className="font-mono text-lg font-bold">{assigned}</span> con almeno un gruppo</span>
            <span className="text-zinc-300">·</span>
            <span className="text-amber-700" data-testid="summary-no-group"><span className="font-mono text-lg font-bold">{total - assigned}</span> senza gruppo</span>
          </div>
        );
      })()}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => {
          const gw = groupWeapons(g);
          if (weaponFilter !== "all") {
            if (weaponFilter === "general" && gw.length > 0) return null;
            if (weaponFilter !== "general" && !gw.includes(weaponFilter)) return null;
          }
          const groupAthletes = activeAthletes.filter((a) => (a.group_ids?.length ? a.group_ids : (a.group_id ? [a.group_id] : [])).includes(g.id));
          const filteredForGroup = filterByCategory(groupAthletes, categoryFilter);
          const count = filteredForGroup.length;
          const totalCount = groupAthletes.length;
          const coach = coaches.find((c) => c.id === g.coach_id);
          if (categoryFilter !== "all" && count === 0) return null;
          return (
            <div key={g.id} data-testid={`group-${g.id}`} className="bg-white border border-zinc-200 rounded-md p-5 relative group">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Users2 className="w-5 h-5 text-zinc-400" />
                  <h3 className="font-heading font-semibold text-lg">{g.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`group-edit-${g.id}`} onClick={() => openEdit(g)} title="Modifica" className="p-2 rounded hover:bg-zinc-100 text-zinc-500 hover:text-black"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`group-delete-${g.id}`} onClick={() => setConfirmDelete(g)} title="Elimina" className="p-2 rounded hover:bg-red-50 text-zinc-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="space-y-1 text-sm text-zinc-600">
                <div className="font-mono text-xs">{g.schedule || "—"}</div>
                <div className="flex gap-2 mt-2 items-center flex-wrap">
                  <GroupWeaponSummary weapons={gw} primary={g.weapon} />
                  {g.level && <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-zinc-100 rounded">{g.level}</span>}
                </div>
                {coach && <div className="text-xs text-zinc-500 mt-2">Coach: {coach.name}</div>}
                <div className="mt-3 font-mono text-2xl">
                  {count}
                  {categoryFilter !== "all" && <span className="text-sm text-zinc-400"> / {totalCount}</span>}
                  <span className="text-xs text-zinc-500 ml-1">atleti{categoryFilter !== "all" ? " in categoria" : ""}</span>
                </div>
              </div>
            </div>
          );
        })}
        {groups.length === 0 && <div className="text-zinc-500 col-span-full">Nessun gruppo creato.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Modifica gruppo" : "Nuovo gruppo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome gruppo</Label><Input data-testid="group-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
            <div><Label>Orario</Label><Input placeholder="es. Lun/Mer 17-19" value={form.schedule || ""} onChange={(e) => setForm({...form, schedule: e.target.value})} /></div>

            <div>
              <Label>Armi praticate</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {WEAPON_CODES.map((code) => {
                  const selected = form.weapons.includes(code);
                  const primary = form.weapon === code;
                  return (
                    <div key={code} className="space-y-1">
                      <button
                        type="button"
                        data-testid={`group-weapon-${code}`}
                        onClick={() => toggleWeapon(code)}
                        className={`w-full h-14 rounded-md border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                          selected
                            ? (primary ? "bg-black text-white border-black" : "bg-zinc-100 text-zinc-800 border-zinc-300")
                            : "bg-white text-zinc-400 border-dashed border-zinc-300 hover:border-zinc-400"
                        }`}
                      >
                        <input type="checkbox" checked={selected} readOnly className="w-4 h-4 accent-black" />
                        {cap(code)}
                      </button>
                      {selected && (
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                          <input type="radio" name="group-primary-weapon" data-testid={`group-primary-${code}`} checked={primary} onChange={() => setPrimary(code)} />
                          Principale
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">Facoltativo · lascia vuoto per attività generale (es. preparazione atletica).</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Livello</Label><Input value={form.level || ""} onChange={(e) => setForm({...form, level: e.target.value})} /></div>
              <div>
                <Label>Maestro / Coach</Label>
                <Select value={form.coach_id || "none"} onValueChange={(v) => setForm({...form, coach_id: v === "none" ? null : v})}>
                  <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editingId && (() => {
              const term = memberSearch.trim().toLowerCase();
              let list = activeAthletes.filter((a) => {
                const eg = effGroups(a);
                if (memberFilter === "none" && eg.length > 0) return false;
                if (memberFilter === "in" && !eg.includes(editingId)) return false;
                if (memberFilter === "other" && (eg.length === 0 || eg.includes(editingId))) return false;
                if (memberWeapon !== "all" && a.weapon !== memberWeapon) return false;
                if (term && !matchesSearch(a, term)) return false;
                return true;
              });
              list = sortAthletes(list);
              const listIds = list.map((a) => a.id);
              const selectAll = () => setMemberSel((prev) => new Set([...prev, ...listIds]));
              const deselectAll = () => setMemberSel((prev) => { const n = new Set(prev); listIds.forEach((id) => n.delete(id)); return n; });
              const groupNames = (a) => {
                const names = effGroups(a).map((id) => groups.find((x) => x.id === id)?.name).filter(Boolean);
                return names;
              };
              return (
                <div className="border-t border-zinc-200 pt-4 mt-2" data-testid="group-members-section">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-heading font-semibold uppercase tracking-wider">Atleti del gruppo</Label>
                    <span className="text-xs font-mono text-zinc-600"><span data-testid="members-results-count">{list.length} risultati</span> · <span data-testid="members-selected-count">{memberSel.size} selezionati</span></span>
                  </div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-400" />
                    <Input data-testid="member-search" className="pl-8 h-9" placeholder="Cerca nome/cognome…" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Select value={memberFilter} onValueChange={setMemberFilter}>
                      <SelectTrigger data-testid="member-filter" className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti</SelectItem>
                        <SelectItem value="none">Senza gruppo</SelectItem>
                        <SelectItem value="in">In questo gruppo</SelectItem>
                        <SelectItem value="other">In altri gruppi</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={memberWeapon} onValueChange={setMemberWeapon}>
                      <SelectTrigger data-testid="member-weapon" className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le armi</SelectItem>
                        {WEAPON_CODES.map((w) => <SelectItem key={w} value={w}>{cap(w)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" data-testid="member-select-all" onClick={selectAll}><CheckSquare className="w-3 h-3 mr-1" />Seleziona risultati</Button>
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" data-testid="member-deselect-all" onClick={deselectAll}><Square className="w-3 h-3 mr-1" />Deseleziona risultati</Button>
                  </div>
                  <div className="border border-zinc-200 rounded-md max-h-80 overflow-y-auto divide-y divide-zinc-100" data-testid="member-list">
                    {list.map((a) => {
                      const on = memberSel.has(a.id);
                      const names = groupNames(a);
                      return (
                        <label key={a.id} data-testid={`member-row-${a.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50">
                          <input type="checkbox" data-testid={`member-check-${a.id}`} checked={on}
                            onChange={() => setMemberSel((prev) => { const n = new Set(prev); on ? n.delete(a.id) : n.add(a.id); return n; })}
                            className="w-4 h-4 accent-black" />
                          <span className="text-sm font-medium flex-1">{displayName(a)}</span>
                          {names.length === 0
                            ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-800 rounded" data-testid={`member-nogroup-${a.id}`}>Senza gruppo</span>
                            : <span className="text-[11px] text-zinc-500 text-right">{names.join(" · ")}</span>}
                        </label>
                      );
                    })}
                    {list.length === 0 && <div className="px-3 py-6 text-center text-sm text-zinc-400">Nessun atleta corrisponde ai filtri.</div>}
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="group-save" onClick={save} className="bg-black hover:bg-zinc-800">{editingId ? "Aggiorna" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il gruppo?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? `Stai per eliminare "${confirmDelete.name}". Gli atleti assegnati non verranno cancellati ma perderanno il gruppo.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-group" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
